import { useCallback, useRef } from "react";

import {
  streamIxdPreviewCompare,
  type IxdPreviewCompareSourceSpec,
} from "@/apis/compare/route";
import { isInvalidIxdStructureError } from "@/apis/errors";
import { UploadFailedModal } from "@/components/modals/upload-failed-modal";
import { pushModal } from "@/stores/modal";
import { useIxdPreviewCompareStore } from "@/stores/ixd-preview-compare";

/**
 * 헤더에 표시할 소스 라벨. 업로드 파일은 파일명을, ID 기반 소스는 유형명을 쓴다
 * (백엔드 summary 에 파일명이 없어 입력 spec 에서만 알 수 있다).
 */
const describeSource = (spec: IxdPreviewCompareSourceSpec): string => {
  switch (spec.type) {
    case "ixd_file":
      return spec.ixdFile?.name ?? "IXD 업로드";
    case "xlsx":
      return spec.xlsFile?.name ?? "XBRL 편집기 xlsx";
    case "ixd_report":
      return "편집용 IXD";
    case "editable_treeview":
      return "최종산출물";
  }
};

/**
 * NDJSON 스트림을 소비해 스토어에 summary/TOC 를 점진 push 한다.
 * 도착 데이터는 스토어에만 보관(SSOT)하고, 중단/언마운트는 AbortController 로 처리.
 */
export const useIxdPreviewCompareStream = () => {
  const start = useIxdPreviewCompareStore((s) => s.start);
  const addToc = useIxdPreviewCompareStore((s) => s.addToc);
  const setSources = useIxdPreviewCompareStore((s) => s.setSources);
  const finish = useIxdPreviewCompareStore((s) => s.finish);
  const fail = useIxdPreviewCompareStore((s) => s.fail);
  const abortRef = useRef<AbortController | null>(null);

  const run = useCallback(
    async (
      src: IxdPreviewCompareSourceSpec,
      trg: IxdPreviewCompareSourceSpec,
    ) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      start();
      setSources(describeSource(src), describeSource(trg));
      try {
        for await (const chunk of streamIxdPreviewCompare(
          src,
          trg,
          controller.signal,
        )) {
          if (chunk.type === "toc") addToc(chunk);
        }
        if (!controller.signal.aborted) finish();
      } catch (error) {
        if (controller.signal.aborted) return;
        if (isInvalidIxdStructureError(error)) {
          pushModal(UploadFailedModal);
          fail("제출용 IXD 파일은 업로드할 수 없습니다.");
          return;
        }
        fail(
          error instanceof Error
            ? error.message
            : "비교 중 오류가 발생했습니다.",
        );
      }
    },
    [start, addToc, setSources, finish, fail],
  );

  return { run };
};
